export type OrganizationNameRecord = {
  name?: unknown;
};

export type OrganizationTotalsRequest = {
  name: string;
  page: number;
  pageSize: number;
  sortField: string;
  sortAscending: boolean;
};

export type OrganizationTotalsResponse<T> = {
  page?: unknown;
  pageSize?: unknown;
  total?: unknown;
  data?: T[] | null;
};

const EXACT_ORGANIZATION_LOOKUP_PAGE_SIZE = 500;

const normalizeOrganizationName = (value: unknown) => String(value ?? '').trim().toLowerCase();

const toNonNegativeInteger = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

const toPositiveInteger = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export async function findOrganizationByExactName<T extends OrganizationNameRecord>(
  getOrganizationsTotals: (
    request: OrganizationTotalsRequest,
  ) => Promise<OrganizationTotalsResponse<T>>,
  requestedName: unknown,
): Promise<T | null> {
  const name = String(requestedName ?? '').trim();
  const normalizedName = normalizeOrganizationName(name);
  if (!normalizedName) return null;

  let page = 0;

  while (true) {
    const response = await getOrganizationsTotals({
      name,
      page,
      pageSize: EXACT_ORGANIZATION_LOOKUP_PAGE_SIZE,
      sortField: 'name',
      sortAscending: true,
    });
    const organizations = Array.isArray(response?.data) ? response.data : [];
    const exactMatch = organizations.find(
      (organization) => normalizeOrganizationName(organization?.name) === normalizedName,
    );
    if (exactMatch) return exactMatch;

    const responsePage = Math.max(page, toNonNegativeInteger(response?.page, page));
    const responsePageSize = toPositiveInteger(
      response?.pageSize,
      EXACT_ORGANIZATION_LOOKUP_PAGE_SIZE,
    );
    const total = toNonNegativeInteger(response?.total, organizations.length);
    const nextPage = responsePage + 1;

    if (organizations.length === 0 || nextPage * responsePageSize >= total) {
      return null;
    }

    page = nextPage;
  }
}
